import { useSyncExternalStore } from 'react';
import { AlertTriangle, Check, HelpCircle, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { Aviso, TipoDeAviso } from './avisos';
import { descartar, leer, suscribir } from './avisos';

interface Variante {
  titulo: string;
  borde: string;
  texto: string;
  fondoIcono: string;
  anillo: string;
  icono: LucideIcon;
}

const VARIANTES: Record<TipoDeAviso, Variante> = {
  exito: {
    titulo: '¡Éxito!',
    borde: 'border-l-emerald-500',
    texto: 'text-emerald-600',
    fondoIcono: 'bg-emerald-500',
    anillo: 'ring-emerald-200',
    icono: Check,
  },
  advertencia: {
    titulo: '¡Advertencia!',
    borde: 'border-l-orange-500',
    texto: 'text-orange-600',
    fondoIcono: 'bg-orange-500',
    anillo: 'ring-orange-200',
    icono: AlertTriangle,
  },
  error: {
    titulo: '¡Error!',
    borde: 'border-l-rose-500',
    texto: 'text-rose-600',
    fondoIcono: 'bg-rose-500',
    anillo: 'ring-rose-200',
    icono: X,
  },
  informacion: {
    titulo: '¡Información!',
    borde: 'border-l-blue-500',
    texto: 'text-blue-600',
    fondoIcono: 'bg-blue-500',
    anillo: 'ring-blue-200',
    icono: HelpCircle,
  },
};

const VACIO: Aviso[] = [];

export function Notificaciones() {
  const avisos = useSyncExternalStore(suscribir, leer, () => VACIO);

  if (avisos.length === 0) {
    return null;
  }

  return (

    <div
      className="pointer-events-none fixed right-4 top-4 z-[100] flex flex-col items-end gap-2"
      aria-live="polite"
    >
      {avisos.map((aviso) => (
        <Tarjeta key={aviso.id} aviso={aviso} />
      ))}
    </div>
  );
}

function Tarjeta({ aviso }: { aviso: Aviso }) {
  const v = VARIANTES[aviso.tipo];
  const Icono = v.icono;
  const urgente = aviso.tipo === 'error' || aviso.tipo === 'advertencia';

  return (
    <div
      role={urgente ? 'alert' : 'status'}
      aria-live={urgente ? 'assertive' : 'polite'}
      className={`pointer-events-auto relative flex w-[360px] max-w-[calc(100vw-2rem)] items-start gap-3 rounded-lg border-l-4 bg-white py-3 pl-4 pr-10 shadow-lg ring-1 ring-black/5 ${v.borde}`}
    >
      <span
        className={`mt-0.5 grid size-9 shrink-0 place-items-center rounded-full ring-4 ${v.fondoIcono} ${v.anillo}`}
      >
        <Icono className="size-5 text-white" strokeWidth={3} aria-hidden />
      </span>

      <div className="min-w-0 flex-1">
        <p className={`text-sm font-bold uppercase ${v.texto}`}>{aviso.titulo ?? v.titulo}</p>
        {aviso.mensaje && (
          <p className="mt-0.5 break-words text-sm uppercase text-armazon-600">
            {aviso.mensaje}
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={() => descartar(aviso.id)}
        aria-label="Cerrar notificación"
        className="absolute right-2 top-2 rounded p-1 text-armazon-400 transition-colors hover:bg-armazon-100 hover:text-armazon-600"
      >
        <X className="size-4" aria-hidden />
      </button>
    </div>
  );
}
